import { useQuery as useOriginalQuery } from '@apollo/react-hooks'
import { OperationVariables } from 'apollo-client'
import { getUsageMetrics } from '../../../modules/devtools/usageMetrics/code-split-import'
import { DocumentNode } from 'graphql'
import {
  QueryHookOptions as OriginalQueryHookOptions,
  QueryResult,
} from 'react-apollo'

import { captureCacheMiss } from './captureCacheMiss'
import { useShouldInvalidatePrevious } from './useShouldInvalidatePrevious'

export type ShouldInvalidateFn<Variables> = (
  previousVariables: Variables | undefined,
  currentVariables: Variables | undefined
) => boolean

export interface QueryExtraOptions<Variables> {
  shouldInvalidatePrevious?: ShouldInvalidateFn<Variables>
}

export interface QueryHookOptions<Data, Variables>
  extends OriginalQueryHookOptions<Data, Variables>,
    QueryExtraOptions<Variables> {}

export function useQuery<Data = any, Variables = OperationVariables>(
  query: DocumentNode,
  options?: QueryHookOptions<Data, Variables>
): QueryResult<Data, Variables> {
  // just omit shouldInvalidatePrevious prop from passing props forward
  const { shouldInvalidatePrevious, ...restOptions } = options || {}
  const result = useOriginalQuery(query, restOptions)

  captureCacheMiss(query, options, result)

  if (useShouldInvalidatePrevious<Data, Variables>(options, result)) {
    Object.assign(result, { data: undefined, error: undefined })
  }

  // exclude this from production build
  if (
    process.env.REACT_APP_ENV === 'dev' ||
    process.env.REACT_APP_ENV === 'test'
  ) {
    const usageMetrics = getUsageMetrics()
    if (usageMetrics.status === 'loaded' && result.data) {
      result.data = usageMetrics?.usageMetrics?.proxyData(result.data, query)
    }
  }

  return result
}
